import { useEffect, useRef, useState } from 'react';

interface UseResponseReadyParams {
    busy: boolean;
    error: string;
    open: boolean;
    onResponse: () => void;
}

/**
 * Tracks when a request finishes: notifies `onResponse` and, if the widget is closed,
 * flags the response as ready until the widget is opened.
 */
export const useResponseReady = ({
    busy,
    error,
    open,
    onResponse,
}: UseResponseReadyParams): boolean => {
    const [responseReady, setResponseReady] = useState(false);
    const requestInFlightRef = useRef(false);
    const onResponseRef = useRef(onResponse);
    onResponseRef.current = onResponse;

    useEffect(() => {
        if (busy) {
            requestInFlightRef.current = true;
            setResponseReady(false);
            return;
        }
        if (!requestInFlightRef.current) return;
        requestInFlightRef.current = false;
        if (error) return;
        if (!open) setResponseReady(true);
        onResponseRef.current();
    }, [busy, error, open]);

    useEffect(() => {
        if (open) setResponseReady(false);
    }, [open]);

    return responseReady;
};
